/**
 * Client-side helper for making authenticated requests directly to the cell API Gateway.
 *
 * Mirrors the pattern used by cors-provenance-api.ts, cors-entity-api.ts,
 * cors-junkdrawer-api.ts, etc. — uses buildCellServiceUrl from shared/cell-session
 * and sends the Cognito access token as Bearer.
 *
 * The access token is used (not the ID token). The pre-token V2 Lambda injects all
 * custom claims (custom:cell_id, custom:tenant_id, etc.) into the access token so
 * the Lambda Authorizer enforces per-user cell/environment/tenant checks.
 * Using the ID token instead bypasses those checks (treated as M2M) — do not do that.
 *
 * Session resolution: Amplify SSR stores Cognito tokens in httpOnly cookies, so
 * client-side `fetchAuthSession()` cannot read them. Use `seedGatewaySession()` after
 * `/api/init`, readable legacy cookies, or `GET /api/session/gateway` (server-side).
 */

import { assertAllowedCellGatewayUrl, fetchAllowedCellGateway } from '@/lib/safe-outbound-url';
import { buildCellServiceUrl } from '@/shared/cell-session';
import { fetchResponseWithAuthRecovery } from '@/shared/cors-auth-recovery';

export interface GatewaySession {
  accessToken: string;
  cellId: string;
  tenantId: string;
}

let _cachedSession: GatewaySession | null = null;
let _serverFetchPromise: Promise<GatewaySession | null> | null = null;

/**
 * Treat a token as stale this many ms before its real `exp`, so we refresh
 * proactively instead of firing a request the gateway will reject. Mirrors the
 * 5-minute pre-expiry refresh window in page.tsx's `msUntilRefresh`.
 */
const TOKEN_EXPIRY_SKEW_MS = 60_000;

/**
 * Whether a Cognito access JWT is expired (or within the skew window). Returns
 * true on any unreadable/exp-less token so a malformed cache entry is dropped
 * and re-resolved rather than sent as a doomed Bearer.
 */
function isAccessTokenExpired(accessJwt: string, nowMs: number = Date.now()): boolean {
  const parts = accessJwt.split('.');
  if (parts.length !== 3) return true;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded)) as Record<string, unknown>;
    const exp = typeof payload.exp === 'number' ? payload.exp : null;
    if (exp === null) return true;
    return exp * 1000 - nowMs <= TOKEN_EXPIRY_SKEW_MS;
  } catch {
    return true;
  }
}

/** Cache tokens from `/api/init` so client code can call the cell API without Amplify. */
export function seedGatewaySession(session: GatewaySession): void {
  if (session.accessToken?.trim() && session.cellId?.trim()) {
    _cachedSession = session;
  }
}

export function clearGatewaySession(): void {
  _cachedSession = null;
  _serverFetchPromise = null;
}

function readDocumentCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length !== 2) return null;
  return parts.pop()?.split(';').shift() ?? null;
}

/** Parse a Cognito access JWT into gateway session fields (client-side, no verify). */
export function gatewaySessionFromAccessJwt(accessJwt: string): GatewaySession | null {
  const trimmed = accessJwt.trim();
  if (!trimmed) return null;
  try {
    const parts = trimmed.split('.');
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded)) as Record<string, unknown>;
    const cellId = (payload['custom:cell_id'] as string | undefined) || '';
    const tenantId = (payload['custom:tenant_id'] as string | undefined) || '';
    if (!cellId.trim()) return null;
    return { accessToken: trimmed, cellId, tenantId };
  } catch {
    return null;
  }
}

function sessionFromReadableCookies(): GatewaySession | null {
  for (const name of ['cognito_access_token', 'x-amzn-oidc-accesstoken'] as const) {
    const jwt = readDocumentCookie(name);
    if (!jwt) continue;
    const parsed = gatewaySessionFromAccessJwt(jwt);
    if (parsed) return parsed;
  }
  return null;
}

async function fetchGatewaySessionFromServer(): Promise<GatewaySession | null> {
  if (!_serverFetchPromise) {
    _serverFetchPromise = (async () => {
      try {
        const res = await fetch('/api/session/gateway', {
          credentials: 'include',
          cache: 'no-store',
        });
        if (!res.ok) return null;
        const data = (await res.json()) as Partial<GatewaySession>;
        if (!data.accessToken?.trim() || !data.cellId?.trim()) return null;
        const session: GatewaySession = {
          accessToken: data.accessToken,
          cellId: data.cellId,
          tenantId: data.tenantId ?? '',
        };
        _cachedSession = session;
        return session;
      } catch (error) {
        console.error('getGatewaySession: /api/session/gateway failed:', error);
        return null;
      } finally {
        _serverFetchPromise = null;
      }
    })();
  }
  return _serverFetchPromise;
}

/**
 * Resolve the current session into the fields needed for API Gateway calls.
 *
 * Does not use client-side Amplify `fetchAuthSession()` — httpOnly auth cookies
 * are invisible to browser JS in the SSR OAuth flow.
 */
export async function getGatewaySession(): Promise<GatewaySession | null> {
  // A cached token that has expired (or is within the skew window) must not be
  // sent — the gateway would 401 and the cors-* clients swallow that silently.
  // Drop it and re-resolve from the server, which returns a freshly-refreshed token.
  if (_cachedSession && isAccessTokenExpired(_cachedSession.accessToken)) {
    clearGatewaySession();
  }
  if (_cachedSession) return _cachedSession;

  const fromCookies = sessionFromReadableCookies();
  if (fromCookies && !isAccessTokenExpired(fromCookies.accessToken)) {
    _cachedSession = fromCookies;
    return fromCookies;
  }

  return fetchGatewaySessionFromServer();
}

/**
 * The current access token, synchronously, or null when none is cached.
 *
 * Exists for the one caller that cannot await: the integration launcher submits a
 * `<form target="_blank">` from inside a click handler (see
 * `components/uploaders-menu.tsx`), and any `await` before `form.submit()` moves
 * the submit out of the user-gesture task, at which point popup blockers kill the
 * new window. So the token has to be readable without a round-trip.
 *
 * Same first two tiers as {@link getGatewaySession} — live cache, then readable
 * cookies — and deliberately NOT the third: `fetchGatewaySessionFromServer` is a
 * network call and cannot be made synchronous. In practice the cache is warm,
 * because the app shell seeds it from every `/api/init` and reschedules a refresh
 * before expiry (`applyInitData` in app/page.tsx). A null here means a genuinely
 * cold or just-expired cache, which the caller handles by letting the integration
 * app reject the handoff rather than by silently sending a doomed token.
 */
export function getCachedAccessToken(): string | null {
  if (_cachedSession && isAccessTokenExpired(_cachedSession.accessToken)) {
    clearGatewaySession();
  }
  if (_cachedSession) return _cachedSession.accessToken;

  const fromCookies = sessionFromReadableCookies();
  if (fromCookies && !isAccessTokenExpired(fromCookies.accessToken)) {
    _cachedSession = fromCookies;
    return fromCookies.accessToken;
  }
  return null;
}

/**
 * Make an authenticated fetch directly to the cell API Gateway.
 *
 * @param servicePrefix  Service path prefix, e.g. `/edge-lambda-service`
 * @param subPath        Path under the prefix, e.g. `/ops-log`
 * @param init           Standard RequestInit — do not set Authorization (injected automatically)
 * @param sessionOverride Pre-resolved session if already available (avoids a second resolve)
 */
export async function gatewayFetch(
  servicePrefix: string,
  subPath: string,
  init: RequestInit = {},
  sessionOverride?: GatewaySession,
): Promise<Response> {
  const session = sessionOverride ?? (await getGatewaySession());
  if (!session) {
    return new Response(JSON.stringify({ error: 'Unauthorized' }), {
      status: 401,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  let url: string;
  try {
    url = buildCellServiceUrl(session.cellId, servicePrefix, subPath);
    // Validate early so we return 400 before attempting auth recovery.
    assertAllowedCellGatewayUrl(url, session.cellId);
  } catch (error) {
    console.error('gatewayFetch: refused outbound URL:', error);
    return new Response(JSON.stringify({ error: 'Invalid gateway request' }), {
      status: 400,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  const cellId = session.cellId;

  // Recover from a stale-token 401/403 by refreshing and retrying with the
  // fresh token. Rebuild headers from the token each attempt.
  return fetchResponseWithAuthRecovery(session.accessToken, (accessToken) => {
    const headers = new Headers(init.headers);
    headers.set('Authorization', `Bearer ${accessToken}`);
    if (!headers.has('Content-Type') && init.body != null) {
      headers.set('Content-Type', 'application/json');
    }
    return fetchAllowedCellGateway(url, cellId, { ...init, headers });
  });
}
